import {
  Flex,
  Grid,
  Text,
  useMediaQuery,
  Table,
  Button,
  Image,
  Input,
  Skeleton,
  Spinner,
} from "@chakra-ui/react";
import { IoMdAdd, IoMdRemove } from "react-icons/io";
import { BsCart2, BsMouse } from "react-icons/bs";
import { useNavigate, useParams } from "react-router-dom";
import useCart from "../../hooks/useCart";
import { PRIMARY_COLOR } from "../../constants/colors";
import { useEffect, useState, useContext } from "react";
import { CiKeyboard } from "react-icons/ci";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { toast } from "react-toastify";
import api from "../../services/api";
import { IoClose } from "react-icons/io5";
import { AuthContext } from "../../contexts/AuthContext";
import { buildLoginPath, buildRegisterPath } from "../../utils/catalogContext";
import { isClientUser } from "../../utils/userRole";

import { useRef } from "react";

const normalizeImageUrl = (path) => {
  if (!path) return "";

  const normalized = path.replace(/\\/g, "/");

  const index = normalized.indexOf("/uploads");
  if (index === -1) return "";

  return `https://api.aqvex.com.br${normalized.substring(index)}`;
};

export default function ProductDetail() {
  const { id, clientHash, sellerId } = useParams();
  const { user } = useContext(AuthContext);

  const [isLargerThan768] = useMediaQuery("(min-width: 768px)");
  const [selectedImage, setSelectedImage] = useState("");
  const [product, setProduct] = useState({
    name: "",
    id: null,
    variations: [],
    price: null,
    prod_referencia: null,
    obs: "",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [allImages, setAllImages] = useState([]);
  const [images, setImages] = useState([]);
  const [activeColor, setActiveColor] = useState("");
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageToFullscreen, setImageToFullscreen] = useState(null);

  const startX = useRef(0);
  const endX = useRef(0);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleStart = (x) => {
    startX.current = x;
  };

  const handleEnd = (x) => {
    endX.current = x;

    const diff = startX.current - endX.current;

    if (Math.abs(diff) < 50) return;

    if (diff > 0) {
      handleImageNavigation("next");
    } else {
      handleImageNavigation("prev");
    }
  };

  const getImagesByColor = async (color, startIndex = 0) => {
    try {
      const response = await api.get(
        `/admin/products/${id}/images/${color}/${clientHash}`,
      );

      const imgs = response.data.data;

      setImages(imgs);
      setActiveColor(color);
      setCurrentImageIndex(startIndex);
      setSelectedImage(imgs[startIndex]?.url_imagem ?? "");
    } catch (error) {
      toast.error("Não foi possível buscar as imagens.");
    }
  };

  const handleSelectColor = (color) => {
    const thumbnail = getColorThumbnail(color);
    setImageToFullscreen(thumbnail);
  };

  useEffect(() => {
    const getProductDetailByClient = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(
          `/client-database/product-variations/${id}/${clientHash}`,
        );

        const colors = [
          ...new Set(
            response.data.data
              .filter(({ qtde }) => qtde > 0)
              .map(({ cor }) => cor),
          ),
        ];

        const imagesByColor = await Promise.all(
          colors.map((color) =>
            api.get(`/admin/products/${id}/images/${color}/${clientHash}`),
          ),
        );

        const mainImage = response.data.data[0]?.url_imagem_principal;

        const normalizedMain = normalizeImageUrl(mainImage);

        let allImages = imagesByColor.flatMap((res) => res.data.data || []);

        allImages = allImages.map((img) => ({
          ...img,
          url_imagem: normalizeImageUrl(img.url_imagem),
        }));

        const filtered = allImages.filter(
          (img) => img.url_imagem !== normalizedMain,
        );

        const finalImages = [
          ...(normalizedMain ? [{ url_imagem: normalizedMain }] : []),
          ...filtered,
        ];
        setAllImages(finalImages);
        setImages(finalImages);
        setSelectedImage(normalizedMain || finalImages[0]?.url_imagem || "");
        setCurrentImageIndex(0);
        // A galeria inicial mescla as imagens de todas as cores na ordem de
        // `colors`, então marcamos a última cor como ativa para que o cálculo
        // de "cor anterior/próxima" no handleImageNavigation comece alinhado.
        setActiveColor(colors[colors.length - 1] ?? "");

        setProduct({
          name: response.data.data[0]?.prod_descricao,
          id: response.data.data[0]?.prod_id,
          prod_referencia: response.data.data[0]?.prod_referencia,
          variations: response.data.data,
          price: response.data.data[0]?.preco,
          obs: response.data.data[0]?.obs ?? "",
        });
      } catch (error) {
        toast.error(
          error?.message ?? "Não foi possível buscar os detalhes do produto.",
        );
      } finally {
        setIsLoading(false);
      }
    };

    getProductDetailByClient();
  }, [clientHash, id]);

  // Busca o carrinho salvo no backend ao entrar na página e salva no
  // backend imediatamente a cada alteração — sem cache local.
  const { cart, addToCart, decrementFromCart, setQuantity } = useCart(
    clientHash,
    sellerId,
  );
  const navigate = useNavigate();

  // Só cliente logado pode montar carrinho. Sem login → tela de login;
  // logado como admin/master → obriga o cadastro como cliente.
  const ensureCanBuy = () => {
    const state = { from: window.location.pathname };
    if (!user) {
      navigate(buildLoginPath(clientHash, sellerId), { state });
      return false;
    }
    if (!isClientUser(user)) {
      toast.warning(
        "Você está logado como administrador. Para comprar, faça o cadastro como cliente.",
      );
      navigate(buildRegisterPath(clientHash, sellerId), { state });
      return false;
    }
    return true;
  };

  const [isKeyboardMode, setIsKeyboardMode] = useState(false);
  const [loadingStockKey, setLoadingStockKey] = useState(null);

  const checkFreshStock = async (color, size) => {
    const key = `${color}-${size}`;
    setLoadingStockKey(key);
    try {
      const response = await api.get(
        `/client-database/product-variations/${id}/${clientHash}`,
      );
      const freshVariations = response.data.data;
      setProduct((prev) => ({ ...prev, variations: freshVariations }));
      const variation = freshVariations.find(
        ({ tam, cor }) => tam === size && cor === color,
      );
      return variation?.qtde ?? 0;
    } finally {
      setLoadingStockKey(null);
    }
  };

  useEffect(() => {
    const index = images.findIndex((img) => img.url_imagem === selectedImage);
    if (index !== -1) {
      setCurrentImageIndex(index);
    }
  }, [images, selectedImage]);

  const handleImageNavigation = (direction) => {
    if (!images?.length) return;

    const currentIndex = images.findIndex(
      (img) => img.url_imagem === selectedImage,
    );

    if (currentIndex === -1) return;

    // A lista `images` já contém as imagens de todas as cores mescladas.
    // Navegamos dentro dela com wraparound, sem trocar a lista carregada
    // (trocar de lista ao cruzar o limite fazia a galeria "encolher"
    // para as imagens de uma única cor).
    const nextIndex =
      direction === "next"
        ? (currentIndex + 1) % images.length
        : (currentIndex - 1 + images.length) % images.length;

    const nextImage = images[nextIndex];
    setSelectedImage(nextImage.url_imagem);
    setCurrentImageIndex(nextIndex);

    if (nextImage.cor_variacao) {
      setActiveColor(nextImage.cor_variacao);
    }
  };

  const totalProductItems = cart
    .filter((item) => item.id === product.id)
    .reduce((sum, item) => {
      const totalQuantity = Object.values(item.sizes).reduce(
        (total, size) => total + (size.quantity || 0),
        0,
      );

      return sum + totalQuantity;
    }, 0);

  const getColorThumbnail = (color) => {
    const variation = product.variations.find(({ cor }) => cor === color);
    return normalizeImageUrl(variation?.url_imagem_variacao);
  };

  const totalProductValue = totalProductItems * product.price;
  const sizes = [...new Set(product.variations.map(({ tam }) => tam))];
  const availableColors = [
    ...new Set(
      product.variations.filter(({ qtde }) => qtde > 0).map(({ cor }) => cor),
    ),
  ];

  return (
    <Flex
      width="100%"
      justifyContent="center"
      alignItems="center"
      minH="100vh"
      paddingTop={isLargerThan768 ? "100px" : "100px"}
      position="relative"
    >
      {imageToFullscreen && (
        <Flex
          position="fixed"
          top={0}
          left={0}
          width="100%"
          height="100%"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          zIndex={999}
          bg="rgba(0,0,0,0.7)"
          backdropFilter="blur(6px)"
          padding={isLargerThan768 ? "0px" : "15px"}
          onClick={() => setImageToFullscreen(null)}
        >
          <Flex position="relative" onClick={(e) => e.stopPropagation()}>
            {/* BOTÃO X */}
            <Flex
              position="absolute"
              top="-40px"
              right={isLargerThan768 ? "-40px" : "0px"}
              cursor="pointer"
              onClick={() => setImageToFullscreen(null)}
            >
              <IoClose size={26} color="#ececec" />
            </Flex>

            <img
              src={imageToFullscreen}
              alt="Imagem"
              style={{
                maxWidth: "90vw",
                maxHeight: isLargerThan768 ? "85vh" : "75vh",
                width: "auto",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Flex>
        </Flex>
      )}
      <Flex
        maxW={isLargerThan768 ? "1200px" : "100%"}
        width="100%"
        filter={imageToFullscreen ? "blur(8px)" : "none"}
      >
        <Grid
          templateColumns={isLargerThan768 ? "1fr 1fr" : "1fr"}
          columnGap={isLargerThan768 ? "20px" : "0px"}
          rowGap={isLargerThan768 ? "0px" : "40px"}
          width="100%"
        >
          {/* Carrossel de imagens */}
          <Flex gap={4} w="100%" overflow="hidden">
            {isLoading ? (
              <>
                <Flex direction="column" gap={2}>
                  {[1, 2, 3].map((i) => (
                    <Skeleton
                      key={i}
                      height="60px"
                      width="60px"
                      borderRadius="md"
                    />
                  ))}
                </Flex>
                <Skeleton flex={1} height="400px" borderRadius="md" />
              </>
            ) : (
              <Flex
                w="100%"
                flexDirection={isLargerThan768 ? "row" : "column"}
                gap="20px"
              >
                {/* THUMBNAILS */}
                <Flex
                  direction={isLargerThan768 ? "column" : "row"}
                  gap={2}
                  overflowX={!isLargerThan768 ? "auto" : "hidden"}
                  overflowY={isLargerThan768 ? "auto" : "hidden"}
                  maxW="100%"
                  pb={!isLargerThan768 ? "10px" : 0}
                  display={isLargerThan768 ? "flex" : "none"}
                >
                  {images.length > 1 && isLargerThan768 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleImageNavigation("prev")}
                    >
                      <FiChevronUp size={24} />
                    </Button>
                  )}

                  {images?.map((img) => (
                    <Image
                      key={img.url_imagem}
                      src={normalizeImageUrl(img.url_imagem)}
                      boxSize={isLargerThan768 ? "80px" : "60px"}
                      minW={isLargerThan768 ? "80px" : "60px"}
                      objectFit="cover"
                      borderRadius="md"
                      border={
                        selectedImage === img.url_imagem
                          ? `2px solid ${PRIMARY_COLOR}`
                          : "1px solid #ccc"
                      }
                      cursor="pointer"
                      onClick={() => setSelectedImage(img.url_imagem)}
                    />
                  ))}

                  {images.length > 1 && isLargerThan768 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleImageNavigation("next")}
                    >
                      <FiChevronDown size={24} />
                    </Button>
                  )}
                </Flex>

                <Flex
                  flex={1}
                  justifyContent="center"
                  alignItems="center"
                  overflow="hidden"
                  onMouseDown={(e) => handleStart(e.clientX)}
                  onMouseUp={(e) => handleEnd(e.clientX)}
                  onTouchStart={(e) => handleStart(e.touches[0].clientX)}
                  onTouchEnd={(e) => handleEnd(e.changedTouches[0].clientX)}
                >
                  <Image
                    src={
                      selectedImage
                        ? normalizeImageUrl(selectedImage)
                        : "/no-image.png"
                    }
                    objectFit="contain"
                    borderRadius="md"
                    width="100%"
                    maxW={isLargerThan768 ? "500px" : "100%"}
                    maxH={isLargerThan768 ? "500px" : "300px"}
                    onClick={() =>
                      setImageToFullscreen(normalizeImageUrl(selectedImage))
                    }
                  />
                </Flex>
                {!isLargerThan768 && images.length > 1 && (
                  <Flex justifyContent="center" mt="10px" gap="6px">
                    {images.map((_, index) => (
                      <Flex
                        key={index}
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg={
                          index === currentImageIndex
                            ? PRIMARY_COLOR
                            : "gray.300"
                        }
                        transition="all 0.3s"
                        cursor="pointer"
                        onClick={() => {
                          setSelectedImage(images[index].url_imagem);
                          setCurrentImageIndex(index);
                        }}
                      />
                    ))}
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>

          {!isLargerThan768 && (
            <Flex gap="10px" width="100%" overflow="auto" padding="15px">
              {availableColors.map((color) => {
                const thumbnail = getColorThumbnail(color);
                const colorHex =
                  product.variations.find(({ cor }) => cor === color)
                    ?.cor_hexadecimal ?? "";

                return (
                  <Flex
                    flexDirection="column"
                    alignItems="center"
                    key={color}
                    gap="5px"
                  >
                    <Flex
                      boxSize="40px"
                      minW="40px"
                      minH="40px"
                      borderRadius="full"
                      bg={thumbnail ? undefined : colorHex}
                      bgImage={thumbnail ? `url(${thumbnail})` : undefined}
                      bgSize="cover"
                      bgPosition="center"
                      bgRepeat="no-repeat"
                      border={
                        activeColor === color
                          ? `2px solid ${PRIMARY_COLOR}`
                          : "1px solid #ccc"
                      }
                      onClick={() => {
                        setActiveColor(color);
                        getImagesByColor(color);
                      }}
                      cursor="pointer"
                    />
                    <Text fontSize="12px">{color}</Text>
                  </Flex>
                );
              })}
            </Flex>
          )}
          {/* Detalhes do produto */}
          <Flex flexDirection="column" w="100%" padding="15px" gap="20px">
            {isLoading ? (
              <>
                <Skeleton height="28px" width="60%" />
                <Skeleton height="18px" width="40%" />
                <Skeleton height="22px" width="30%" />
                <Skeleton height="400px" borderRadius="md" />
              </>
            ) : (
              <Flex w="100%" flexDirection="column" gap="20px">
                <div>
                  <Text fontSize="20px" fontWeight={700}>
                    {product.name ?? "-"}
                  </Text>
                  <Text fontSize="14px" color="gray.500">
                    Referência: {product?.prod_referencia ?? "-"}
                  </Text>
                </div>

                <Text fontWeight={600} fontSize="18px" color="green.600">
                  {Intl.NumberFormat("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  }).format(product.price)}
                </Text>
                <Text fontSize="14px">{product?.obs}</Text>
                {isKeyboardMode ? (
                  <BsMouse
                    size={26}
                    onClick={() => setIsKeyboardMode((prev) => !prev)}
                    cursor="pointer"
                  />
                ) : (
                  <CiKeyboard
                    size={26}
                    onClick={() => setIsKeyboardMode((prev) => !prev)}
                    cursor="pointer"
                  />
                )}
                <Flex maxH="450px" overflow="auto" position="relative">
                  <Table.Root showColumnBorder>
                    <Table.Header
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                        background: "white",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                      }}
                    >
                      <Table.Row backgroundColor="white">
                        <Table.ColumnHeader></Table.ColumnHeader>
                        {sizes.map((size) => (
                          <Table.ColumnHeader
                            key={size}
                            padding={isLargerThan768 ? "15px" : "5px"}
                            textAlign="center"
                            style={{
                              position: "sticky",
                              top: 0,
                              zIndex: 10,
                              background: "white",
                            }}
                          >
                            {size}
                          </Table.ColumnHeader>
                        ))}
                      </Table.Row>
                    </Table.Header>

                    <Table.Body>
                      {availableColors.map((color) => (
                        <Table.Row key={color} minH="60px">
                          <Table.Cell
                            padding="15px"
                            textAlign="center"
                            justifyContent="center"
                            display="flex"
                            flexDirection="column"
                          >
                            {(() => {
                              const thumbnail = getColorThumbnail(color);
                              const colorHex =
                                product.variations.find(
                                  ({ cor }) => cor === color,
                                )?.cor_hexadecimal ?? "";

                              return (
                                <Flex
                                  boxSize="40px"
                                  minW="40px"
                                  minH="40px"
                                  borderRadius="full"
                                  bg={thumbnail ? undefined : colorHex}
                                  bgImage={
                                    thumbnail ? `url(${thumbnail})` : undefined
                                  }
                                  bgSize="cover"
                                  bgPosition="center"
                                  bgRepeat="no-repeat"
                                  border={
                                    activeColor === color
                                      ? `2px solid ${PRIMARY_COLOR}`
                                      : "1px solid #ccc"
                                  }
                                  onClick={() => {
                                    handleSelectColor(color);
                                  }}
                                  cursor="pointer"
                                />
                              );
                            })()}
                            <Text fontSize="12px" mt="5px">
                              {color}
                            </Text>
                          </Table.Cell>

                          {sizes.map((size) => {
                            const cartItem = cart.find(
                              (item) =>
                                item.id === product.id && item.color === color,
                            );

                            const quantity =
                              cartItem?.sizes[size]?.quantity ?? 0;

                            const selectedVariation = product.variations.find(
                              ({ tam, cor, qtde }) =>
                                tam === size && cor === color && qtde > 0,
                            );

                            const hasStock = Boolean(selectedVariation);

                            return (
                              <Table.Cell
                                key={size}
                                padding="15px"
                                textAlign="center"
                              >
                                {isKeyboardMode ? (
                                  <Flex
                                    align="center"
                                    justify="center"
                                    minH="60px"
                                    flexDirection="column"
                                    backgroundColor={
                                      !hasStock ? "gray.200" : ""
                                    }
                                  >
                                    {hasStock && (
                                      <Input
                                        type="number"
                                        min={0}
                                        max={selectedVariation.qtde}
                                        value={quantity}
                                        onChange={(e) => {
                                          if (!ensureCanBuy()) return;
                                          const value = Math.max(
                                            0,
                                            Math.min(
                                              selectedVariation.qtde,
                                              Number(e.target.value),
                                            ),
                                          );

                                          setQuantity({
                                            id: product.id,
                                            color,
                                            size,
                                            quantity: value,
                                            provar_id:
                                              selectedVariation.provar_id,
                                            qtde: selectedVariation.qtde,
                                            price: product.price,
                                          });
                                        }}
                                        textAlign="center"
                                        width="60px"
                                        size="sm"
                                      />
                                    )}
                                  </Flex>
                                ) : !hasStock ? (
                                  <Flex
                                    minH="60px"
                                    align="center"
                                    justify="center"
                                    backgroundColor="gray.200"
                                  />
                                ) : quantity === 0 ? (
                                  <Flex
                                    direction="column"
                                    align="center"
                                    justify="center"
                                    gap={1}
                                    minH="60px"
                                  >
                                    {loadingStockKey === `${color}-${size}` ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <IoMdAdd
                                        cursor="pointer"
                                        size={22}
                                        onClick={async () => {
                                          if (!ensureCanBuy()) return;
                                          const freshStock = await checkFreshStock(color, size);
                                          if (freshStock <= 0) {
                                            toast.warning("Produto sem estoque.");
                                            return;
                                          }
                                          addToCart({
                                            id: product.id,
                                            name: product.name,
                                            price: product.price,
                                            color,
                                            size,
                                            ...selectedVariation,
                                            qtde: freshStock,
                                          });
                                        }}
                                      />
                                    )}
                                  </Flex>
                                ) : (
                                  <Flex
                                    direction="column"
                                    align="center"
                                    justify="center"
                                    gap={1}
                                    minH="60px"
                                  >
                                    {loadingStockKey === `${color}-${size}` ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      <IoMdAdd
                                        cursor="pointer"
                                        size={18}
                                        onClick={async () => {
                                          if (!ensureCanBuy()) return;
                                          const freshStock = await checkFreshStock(color, size);
                                          if (quantity >= freshStock) {
                                            toast.warning("Estoque máximo");
                                            return;
                                          }
                                          addToCart({
                                            id: product.id,
                                            name: product.name,
                                            price: product.price,
                                            color,
                                            size,
                                            ...selectedVariation,
                                            qtde: freshStock,
                                          });
                                        }}
                                      />
                                    )}
                                    <Text fontSize="sm">{quantity}</Text>
                                    <IoMdRemove
                                      cursor="pointer"
                                      size={18}
                                      onClick={() =>
                                        decrementFromCart(
                                          product.id,
                                          color,
                                          size,
                                        )
                                      }
                                    />
                                  </Flex>
                                )}
                              </Table.Cell>
                            );
                          })}
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                </Flex>
                {/* Tabela de tamanhos */}

                <Flex width="100%" justifyContent="space-between">
                  <Text>{totalProductItems} pç</Text>
                  <Text>
                    {Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    }).format(totalProductValue)}
                  </Text>
                </Flex>

                <Flex
                  width="100%"
                  gap="10px"
                  flexDirection={{ base: "column", md: "row" }}
                >
                  <Button
                    flex={1}
                    variant="outline"
                    onClick={() =>
                      navigate(`/catalogo/${clientHash}/${sellerId}`)
                    }
                    padding="10px"
                  >
                    <BsCart2 />
                    Voltar
                  </Button>
                  {totalProductItems > 0 && (
                    <Button
                      flex={1}
                      backgroundColor={PRIMARY_COLOR}
                      onClick={() =>
                        navigate(`/carrinho/${clientHash}/${sellerId}`)
                      }
                      padding="10px"
                    >
                      Finalizar compra
                    </Button>
                  )}
                </Flex>
              </Flex>
            )}
          </Flex>
        </Grid>
      </Flex>
    </Flex>
  );
}
